<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { IonModal, IonButton, IonCard, IonCardHeader, IonCardContent, IonLabel, IonText } from '@ionic/vue';
import type { WorkoutEvent } from '@/api/workout/types/workout';

const props = defineProps<{
  open: boolean;
  workout: WorkoutEvent | null;
  busy?: boolean;
}>();

const emit = defineEmits<{
  resume: [];
  'finish-now': [];
  discard: [];
  dismiss: [];
}>();

const { t } = useI18n();

const isOpen = computed({
  get: () => props.open && !!props.workout,
  set: value => {
    if (!value) emit('dismiss');
  },
});

const title = computed(() => props.workout?.name || t('workout-recovery.title'));
</script>

<template>
  <!--
    Centered card modal (same pattern as app-confirm): auto width/height,
    backdrop over the app shell, safe-area padding so content clears status/nav bars.
  -->
  <ion-modal
    :is-open="isOpen"
    :show-backdrop="true"
    :backdrop-dismiss="true"
    class="workout-recovery-modal"
    @did-dismiss="emit('dismiss')"
  >
    <ion-card class="recovery-card bg-medium-dark m-0">
      <ion-card-header class="flex flex-col text-tertiary mb-4 text-center">
        <ion-label class="text-h4 p-0 mb-2">{{ title }}</ion-label>
        <ion-text class="text-p">{{ t('workout-recovery.subtitle') }}</ion-text>
      </ion-card-header>

      <ion-card-content class="text-tertiary w-full">
        <div class="recovery-actions flex flex-col space-y-4">
          <ion-button expand="block" shape="round" :disabled="busy" @click="emit('resume')">
            {{ t('workout-recovery.resume') }}
          </ion-button>
          <ion-button expand="block" shape="round" fill="outline" :disabled="busy" @click="emit('finish-now')">
            {{ t('workout-recovery.finish-now') }}
          </ion-button>
          <ion-button
            expand="block"
            shape="round"
            fill="clear"
            color="danger"
            :disabled="busy"
            @click="emit('discard')"
          >
            {{ t('workout-recovery.discard') }}
          </ion-button>
        </div>
      </ion-card-content>
    </ion-card>
  </ion-modal>
</template>

<style lang="scss" scoped>
.recovery-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  max-height: min(80vh, 520px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

ion-modal.workout-recovery-modal {
  --box-shadow: none;
  --width: min(92vw, 400px);
  --height: auto;
  --max-height: calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 48px);
  --border-radius: 0.5rem;
  --background: #2c2c2c;
  --backdrop-opacity: 0.55;

  /* Keep the card in the usable viewport (below status bar / above home indicator). */
  align-items: center;
  justify-content: center;
  padding-top: max(16px, env(safe-area-inset-top, 0px));
  padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
  padding-left: max(16px, env(safe-area-inset-left, 0px));
  padding-right: max(16px, env(safe-area-inset-right, 0px));
}
</style>
